import { useEffect, useId, useState } from "react";
import { INSTRUCTION_LIMIT } from "@broadpaper/core/ai";
import { useSession, useSelection } from "../state/context.js";
import { useObservable } from "../state/observable.js";
import type { AiStatus } from "../state/ai.js";
import { Button, Segmented, TextArea } from "../ui/primitives.js";
import { Icon } from "../ui/icons.js";

/**
 * Describe a report, or ask for a change, and get a proposal back.
 *
 * The panel only asks. What comes back is shown over the canvas by
 * `AiProposal`, where it is accepted or discarded; nothing here writes to the
 * document, which is what makes "never silently overwrite the user's work"
 * something the structure guarantees rather than something each button has to
 * remember.
 */
export function AiPanel() {
  const session = useSession();
  const ai = session.ai;
  const state = useObservable(ai.state);
  const selection = useSelection();
  const inputId = useId();
  const countId = useId();

  // On open, and again after every generation (the controller does that one):
  // the allowance is the host's to measure and changes behind the panel's back.
  useEffect(() => {
    void ai.refreshAllowance();
  }, [ai]);

  const { status, mode, instruction, allowance } = state;
  const running = status.phase === "running";
  const proposing = status.phase === "proposal";
  const length = instruction.length;
  const canAsk = !running && !proposing && instruction.trim().length > 0 && length <= INSTRUCTION_LIMIT;

  return (
    <div className="bpe-ai">
      <Segmented
        exclusive
        ariaLabel="What to ask for"
        value={mode}
        onChange={(v) => ai.setMode(v as typeof mode)}
        options={[
          { value: "update", label: "Edit report" },
          { value: "create", label: "New report" }
        ]}
      />

      {mode === "update" ? (
        selection.length ? (
          <p className="bpe-ai__scope">
            <Icon name="info" size={13} />
            <span>
              Changes stay inside the {selection.length === 1 ? "selected block" : `${selection.length} selected blocks`}.{" "}
              <button type="button" className="bpe-link" onClick={() => session.store.select([])}>
                Use the whole report
              </button>
            </span>
          </p>
        ) : (
          <p className="bpe-ai__hint">Describe a change to the whole report, or select blocks first to change only those.</p>
        )
      ) : (
        <p className="bpe-ai__hint">Say what the report is for and what it should show. The new design replaces this one only if you accept it.</p>
      )}

      <label className="bpe-ai__label" htmlFor={inputId}>
        {mode === "create" ? "Describe the report" : "What should change?"}
      </label>
      <TextArea
        id={inputId}
        rows={6}
        value={instruction}
        maxLength={INSTRUCTION_LIMIT}
        disabled={running}
        aria-describedby={countId}
        placeholder={mode === "create" ? "A monthly client report with a holdings table grouped by asset class and a performance chart" : "Add a holdings table grouped by asset class"}
        onChange={(e) => ai.setInstruction(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canAsk) {
            e.preventDefault();
            void ai.generate();
          }
        }}
      />
      <div id={countId} className={length >= INSTRUCTION_LIMIT ? "bpe-ai__count bpe-ai__count--full" : "bpe-ai__count"}>
        {length.toLocaleString("en-GB")} / {INSTRUCTION_LIMIT.toLocaleString("en-GB")}
      </div>

      {allowance ? <Allowance used={allowance.used} label={allowance.label} /> : null}

      <div className="bpe-ai__actions">
        {running ? (
          <Button onClick={() => ai.cancel()}>Cancel</Button>
        ) : (
          <Button variant="primary" icon="sparkles" disabled={!canAsk} onClick={() => void ai.generate()}>
            {mode === "create" ? "Design it" : "Make the change"}
          </Button>
        )}
      </div>

      <Status status={status} onDismiss={() => ai.dismiss()} />
    </div>
  );
}

function Allowance({ used, label }: { used: number; label: string }) {
  const fraction = Math.min(1, Math.max(0, used));
  return (
    <div className="bpe-ai__allowance">
      <div className="bpe-ai__bar" role="meter" aria-label="AI allowance used this month" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(fraction * 100)} aria-valuetext={label}>
        <span style={{ width: `${fraction * 100}%` }} />
      </div>
      <span className="bpe-ai__allowance-label">{label}</span>
    </div>
  );
}

/**
 * Seconds since the request went. A whole template takes a model the better part
 * of a minute to write, and a spinner with nothing moving beside it looks, by the
 * thirtieth second, like something that has stopped.
 */
function Elapsed() {
  const [start] = useState(() => Date.now());
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const s = Math.floor((Date.now() - start) / 1000);
  return s < 3 ? null : <span className="bpe-ai__elapsed" aria-hidden="true">{s}s</span>;
}

function Status({ status, onDismiss }: { status: AiStatus; onDismiss: () => void }) {
  switch (status.phase) {
    case "idle":
      return null;
    case "running":
      return (
        <div className="bpe-ai__status" role="status">
          <span className="bpe-spinner" aria-hidden="true" />
          {/* A repair is the model being told exactly what failed validation;
              saying so explains why the wait has doubled. */}
          <span>{status.attempt === 0 ? "Designing…" : `Fixing what didn't pass the checks — try ${status.attempt + 1} of 3…`}</span>
          <Elapsed />
        </div>
      );
    case "proposal":
      return (
        <div className="bpe-ai__status" role="status">
          <Icon name="check-circle" size={14} />
          <span>A proposal is open over the page. Accept it or discard it there.</span>
        </div>
      );
    case "refused":
      return (
        <div className="bpe-ai__notice" role="status">
          <p>{status.message}</p>
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            OK
          </Button>
        </div>
      );
    case "error":
      return (
        <div className={status.kind === "allowance" ? "bpe-ai__notice" : "bpe-ai__notice bpe-ai__notice--error"} role="alert">
          <p>{status.message}</p>
          {status.hint ? <p className="bpe-muted">{status.hint}</p> : null}
          <div className="bpe-ai__notice-actions">
            {status.action ? (
              <a className="bpe-btn bpe-btn--sm bpe-btn--primary" href={status.action.href} target="_blank" rel="noopener">
                {status.action.label}
              </a>
            ) : null}
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              OK
            </Button>
          </div>
        </div>
      );
  }
}
